import { useCallback, useEffect, useRef } from 'react';

type AnyFn<Args extends unknown[]> = (...args: Args) => void;

export function useDebounce<Args extends unknown[]>(
  callback: AnyFn<Args>,
  delayMs: number
) {
  const timeoutRef = useRef<number | null>(null);
  const latestCbRef = useRef(callback);

  useEffect(() => {
    latestCbRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const run = useCallback(
    (...args: Args) => {
      if (delayMs <= 0) {
        latestCbRef.current(...args);
        return;
      }
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        latestCbRef.current(...args);
        timeoutRef.current = null;
      }, delayMs);
    },
    [delayMs]
  );

  useEffect(() => cancel, [cancel]);

  return { run, cancel } as const;
}
